import { getChainId, switchChain } from "@wagmi/core";
import { wagmiConfig } from "@/provider/PrivyProvider.tsx";
import { Chip, Popover } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { type Config } from "wagmi";

import WifiIcon from "@mui/icons-material/Wifi";
import SignalWifiBadIcon from "@mui/icons-material/SignalWifiBad";
import { TEST_NET_ID } from "@/types/enum";
import { useTranslation } from "react-i18next";

export default function NetworkSituation() {
  const {t} = useTranslation();
  const chainId = getChainId(wagmiConfig as Config);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [alertContent, setAlertContent] = useState({
    severity: "success" as "success" | "warning",
    message: t("common.connected_network"),
  });
  const [pendingChange, setPendingChange] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  console.log({chainId});

  useEffect(() => {
    // Only show "Network Connected" for 2 seconds after switching to Base
    if (pendingChange) {
      setAlertContent({
        severity: "success",
        message: t("common.connected_network"),
      });
      timeoutRef.current = setTimeout(() => {
        setPendingChange(false);
      }, 2000);
    } else if (!pendingChange && chainId !== TEST_NET_ID.SEPOLIA) {
      setAlertContent({
        severity: "warning",
        message: t("common.wrong_network"),
      });
    } else if (!pendingChange && chainId === TEST_NET_ID.SEPOLIA) {
      setAlertContent({
        severity: "success",
        message: t("common.connected_network"),
      });
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [chainId, pendingChange]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchNetwork = async () => {
    try {
      switchChain(wagmiConfig as Config, {
        chainId: TEST_NET_ID.SEPOLIA,
      });
      setPendingChange(true);
      handleClose(); // Close the Popover after successful network switch
    } catch (error) {
      console.error("Failed to switch network:", error);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  // Choose icon and color based on severity
  const getAlertIcon = () => {
    if (alertContent.severity === "success") {
      return (
        <WifiIcon
          sx={{
            color: "#16a34a",
            mr: { xs: 0, md: 1 }, // No margin on xs, margin on md+
          }}
        />
      );
    }
    return (
      <SignalWifiBadIcon
        sx={{
          color: "#f59e42",
          mr: { xs: 0, md: 1 }, // No margin on xs, margin on md+
        }}
      />
    );
  };

  return (
    <div>
      {chainId === TEST_NET_ID.SEPOLIA ? (
        <Chip
          icon={getAlertIcon()}
          label={
            <span className="hidden md:block">{alertContent.message}</span>
          }
          sx={{
            padding: 1,
            margin: {
              xs: 0,
              md: 1,
            },
            height: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor:
              alertContent.severity === "success"
                ? "rgba(34,197,94,0.08)"
                : "rgba(245,158,66,0.08)",
            color: alertContent.severity === "success" ? "#16a34a" : "#f59e42",
            fontWeight: 600,
            "& .MuiChip-icon": {
              color: "inherit",
              marginRight: { xs: "4px !important", md: "2px !important" },
            },
            "& .MuiChip-label": {
              padding: { xs: 0, md: "0 8px" },
            },
          }}
        />
      ) : (
        <button onClick={handleClick}>
          <Chip
            icon={getAlertIcon()}
            label={
              <span className="hidden md:block">{alertContent.message}</span>
            }
            sx={{
              padding: 0.5,
              margin: {
                xs: 0,
                md: 1,
              },
              height: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor:
                alertContent.severity === "success"
                  ? "rgba(34,197,94,0.08)"
                  : "rgba(245,158,66,0.08)",
              color:
                alertContent.severity === "success" ? "#16a34a" : "#f59e42",
              fontWeight: 600,
              "& .MuiChip-icon": {
                color: "inherit",
                marginRight: { xs: "4px !important", md: "2px !important" },
              },
              "& .MuiChip-label": {
                padding: { xs: 0, md: "0" },
              },
            }}
          />
        </button>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <button onClick={handleSwitchNetwork} className={`p-4`}>
          Change network to {chainId === TEST_NET_ID.SEPOLIA ? "Sepolia" : "Base"}
        </button>
      </Popover>
    </div>
  );
}
